import Configuration from "../shared/configuration";
import EndPointEntity from "../shared/endpointentity";
import API from "../shared/API";
import Common from "./common";

// Fetches an ops report page (Audit -> Ads Report) from the WebAPI.
//
// The HTML deliberately does not live under the web root any more: the API reads it off disk and
// only hands it over to a portal user holding the "Ops Review" right. That is why this is a fetch
// with a bearer token rather than an <iframe src>, which could not carry one.
//
// Unlike the other services here the response is raw HTML, not a SingleResponse wrapper, so there
// is nothing to unwrap -- but a 401/403 still arrives as a normal error and is handled below.
export default class OpsReportService {
  constructor() {
    this.config = new Configuration();
    this.endpoint = new EndPointEntity();
    this.common = new Common();
  }

  // name = "ads" (the server keeps the whitelist; an unknown name is a 404)
  async getReport(name, isRetry) {
    const api = new API({ url: this.config.ITEM_URL });
    api.createEntity({ name: this.endpoint.OPS_REPORT });
    let out = {};
    await api.endpoints[this.endpoint.OPS_REPORT]
      .getAll({ params: { name: name }, responseType: "text" })
      .then((response) => { out = { html: response.data }; })
      .catch((err) => {
        const status = err && err.response ? err.response.status : 0;
        out = { statusCode: status || 500 };
      });

    if (out && out.statusCode === 401 && !isRetry) {
      try {
        const response = await this.common.refreshToken();
        this.common.setToken(response);
        return await this.getReport(name, true);
      } catch (e) {
        return { statusCode: 401, message: "Session expired - please log in again." };
      }
    }
    if (out && out.statusCode === 403) {
      return { statusCode: 403, message: "You do not have access to this report." };
    }
    if (out && out.statusCode === 404) {
      return { statusCode: 404, message: "The report has not been built yet." };
    }
    return out;
  }

  // For a report whose scripts need a real document origin (the heatmap's Google Maps). This asks
  // the API to set a short-lived HttpOnly cookie and tells us where to point the frame; the frame
  // navigation then authorises itself with that cookie, because a navigation cannot carry a bearer
  // token. withCredentials matters: the cookie must ride along on the frame request.
  async getViewUrl(name, isRetry) {
    const api = new API({ url: this.config.ITEM_URL });
    api.createEntity({ name: this.endpoint.OPS_REPORT_TICKET });
    let out = {};
    await api.endpoints[this.endpoint.OPS_REPORT_TICKET]
      .getAll({ params: { name: name }, withCredentials: true })
      .then((response) => { out = response.data; })
      .catch((err) => {
        const status = err && err.response ? err.response.status : 0;
        out = { statusCode: status || 500 };
      });

    if (out && out.statusCode === 401 && !isRetry) {
      try {
        const response = await this.common.refreshToken();
        this.common.setToken(response);
        return await this.getViewUrl(name, true);
      } catch (e) {
        return { statusCode: 401, message: "Session expired - please log in again." };
      }
    }
    if (out && out.statusCode === 403) {
      return { statusCode: 403, message: "You do not have access to this report." };
    }
    if (out && out.data) {
      // out.data is an absolute path on the API host, exactly the path the cookie was scoped to.
      // The origin comes from the configured API base so this still works on staging, where the
      // portal and the API are on different ports.
      const origin = this.config.BASE_WEBAPI_URL.split("/").slice(0, 3).join("/");
      return { url: origin + out.data };
    }
    return { statusCode: (out && out.statusCode) || 500, message: "Could not open the report." };
  }

}
